import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container, { ContainerProps } from '@ep/components/Container';
import ContactUsModal from '@ep/components/Layout/Header/ContactUs/ContactUsModal';
import SeeMoreLink from '@ep/components/SeeMoreLink';
import {
  Body2,
  H2Medium,
  H3,
  Subhead2
  } from '@ep/components/Typography';
import theme from '@ep/theme';
import { Gcms_RichText } from '@ep/types/gatsbyTypes';
import checkEmptyTags from '@ep/utils/checkEmptyTags';
import getHtmlProp from '@ep/utils/getHtmlProp';
import React from 'react';


interface ColorProps {
  hex: string;
}
export interface CTAModuleProps extends ContainerProps {
  className?: string;
  textColor: string;
  heading: string;
  headingColor?: ColorProps;
  subheading?: Gcms_RichText;
  subheadingColor?: ColorProps;
  buttonLabel?: string;
  buttonLink?: string;
  styledLinkLabel?: string;
  styledLinkLink?: string;
  small?: boolean;
  secondarySubheading?: Gcms_RichText;
  secondarySubheadingColor?: ColorProps;
  scrollToLink: string;
  displayDivider?: boolean;
}

const StyledContainer = styled(Container)`
  padding: ${({ background }) => (background ? 10 : 3)}rem 0;
  text-align: center;
  ${({ background, backgroundColors, textColor }) => ` 
        ${background || backgroundColors ? `color: ${theme.colors.white};` : ''}
         h2 {
         color: ${
           background || backgroundColors
             ? theme.colors.white
             : theme.colors.duskBlue
         };
         ${textColor ? `color: ${textColor.hex};` : 'inherit;'}
      }`}
  .ctamodule-text {
    a {
      text-decoration: underline;
      color: ${({ background, backgroundColors }) =>
        background || backgroundColors
          ? theme.colors.white
          : theme.colors.duskBlue};
      /*       &:hover {
        color: ${({ background, backgroundColors }) =>
        background || backgroundColors
          ? theme.colors.white
          : theme.colors.oceanBlue};
      } */
    }
  }

  &.narrowWidth {
    max-width: 700px;

    > div {
      margin-bottom: 2rem;
    }
  }

  ${theme.mediaQueries.tablet} {
    padding: 2.75rem 0;
  }

  ${theme.mediaQueries.mobile} {
    padding: 7.5rem 0;
  }
`;

interface SubheadingProps {
  subheadingColor?: ColorProps;
  secondarySubheadingColor?: ColorProps;
  narrow: boolean;
}
const StyledH2Medium = styled(H2Medium)<{ headingColor?: ColorProps }>`
  ${({ headingColor }) =>
    headingColor?.hex ? `color: ${headingColor.hex} !important;` : `color: ${theme.colors.white} !important;`}
  font-size: 2.5rem;
`;
const StyledH3 = styled(H3)<{ headingColor?: ColorProps }>`
  ${({ headingColor }) =>
    headingColor?.hex ? `color: ${headingColor.hex} !important;` : `color: ${theme.colors.white} !important;`}
`;
const StyledSubheading = styled.div<SubheadingProps>`
  margin-top: 2rem;
  color: ${({ secondarySubheadingColor, subheadingColor }) =>
    secondarySubheadingColor?.hex
      ? `${secondarySubheadingColor.hex} !important;`
      : subheadingColor?.hex
      ? `${subheadingColor.hex} !important;`
      : `${theme.colors.white} !important;`};
  h2 {
    color: ${({ secondarySubheadingColor, subheadingColor }) =>
    secondarySubheadingColor?.hex
      ? `${secondarySubheadingColor.hex} !important;`
      : subheadingColor?.hex
      ? `${subheadingColor.hex} !important;`
      : `${theme.colors.white} !important;`};
  }
  a {
    text-decoration: underline;
  }

  p {
    max-width: 750px;
    margin: 1.5rem auto;
    margin-bottom: 0;
  }

  ${theme.mediaQueries.tablet} {
    margin-top: 0.5rem;
  }

  ${theme.mediaQueries.mobile} {
    margin: 1.5rem auto 0;
    ${({ narrow }) => (narrow ? 'max-width: 16rem;' : '')}
  }
`;

const SubheadingBig = StyledSubheading.withComponent(Subhead2);

const SubheadingSmall = StyledSubheading.withComponent(Body2);

const StyledButton = styled(Button)`
  height: 4rem;
  margin-top: 2rem;
`;

const Divider = styled('div')`
  height: 5rem;
  border-bottom: 1px solid ${theme.colors.oceanBlue};
`;

const StyledSeeMoreLink = styled(SeeMoreLink)`
  margin-top: 1rem;
  font-size: 1.4rem;
  color: ${theme.colors.mediumBlue} !important;
`;

function CTAModule({
  className,
  heading,
  headingColor,
  subheading,
  subheadingColor,
  buttonLabel,
  buttonLink,
  styledLinkLabel,
  styledLinkLink,
  secondarySubheading,
  secondarySubheadingColor,
  small,
  scrollToLink,
  displayDivider,
  ...rest
}: CTAModuleProps) {
  const Heading = small ? StyledH3 : StyledH2Medium;
  const Subheading = small ? SubheadingSmall : SubheadingBig;

  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    // sendToGoogleAnalytics('CTA button clicked - ' + buttonLabel); 
    const contactForm = document.getElementById('contact-form');

    if (contactForm) {
      contactForm.scrollIntoView({ block: 'center' });
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // const sendToGoogleAnalytics = (label: string) => {
  //   dataLayer.push({ event: label });
  // };
  return (
    <StyledContainer id={scrollToLink} className={className} {...rest}>
      {heading && <Heading headingColor={headingColor}>{heading}</Heading>}
      {subheading && checkEmptyTags(subheading.html) && (
        <Subheading
          subheadingColor={subheadingColor}
          narrow={subheading.html!.length < 100}
          {...getHtmlProp(subheading.html)}
        />
      )}
      {buttonLabel &&
        buttonLink &&
        (buttonLink === '#contact-us' ? (
          <>
            <StyledButton
              size={small ? 'medium' : 'large'}
              label={buttonLabel}
              onClick={() => handleOpen()}
            />
            <ContactUsModal open={open} onClose={() => handleClose()} />
          </>
        ) : (
          <StyledButton
            size={small ? 'medium' : 'large'}
            label={buttonLabel}
            link={buttonLink}
            // onClick={() =>
            //   sendToGoogleAnalytics('CTA button clicked - ' + buttonLabel)
            // }
          />
        ))}
      {secondarySubheading && checkEmptyTags(secondarySubheading.html) && (
        <Subheading
          className="ctamodule-text"
          style={{ fontSize: '1.1rem' }}
          secondarySubheadingColor={secondarySubheadingColor}
          narrow={secondarySubheading.html!.length < 100}
          {...getHtmlProp(secondarySubheading.html)}
        />
      )}
      {styledLinkLabel && styledLinkLink && (
        <StyledSeeMoreLink
          color={theme.colors.oceanBlue}
          label={styledLinkLabel}
          to={styledLinkLink ? styledLinkLink : ''}
          // onClick={() =>
          //   sendToGoogleAnalytics('CTA link clicked - ' + styledLinkLabel)
          // }
        />
      )}
      {displayDivider && <Divider />}
    </StyledContainer>
  );
}

export default React.memo(CTAModule);
